import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { State, Store } from '@ngrx/store';
import { IGeoCoords, Location, LocationService, MapsService, RideRequestLocation, Sublocation } from '@uzurv/clara';
import { ApplicationException, AutoCompleteResult, isNullOrUndefined } from '@uzurv/core';
import { Tab } from 'projects/uzurv/src/services/tab/tab.service';
import { Tabbing } from 'projects/uzurv/src/pages/tabbing/tabbing.page';
import { LocationManagerState } from 'projects/uzurv/src/store/location-manager/location-manager.reducer';
import { TabsState } from 'projects/uzurv/src/store/tab/tab.reducer';
import { Observable, combineLatest, map, of, switchMap, take } from 'rxjs';
import * as LocationManagerActions from '../../../store/location-manager/location-manager.actions';
import * as LocationManagerSelectors from '../../../store/location-manager/location-manager.selectors';
import { ConfirmationDialogComponent } from '../../dialogs/confirmation-dialog/confirmation-dialog.component';

// TODO move to Pages and factor out individual components.

@Component({
  selector: 'app-location-manager-page',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './location-manager-page.component.html',
  styleUrls: ['./location-manager-page.component.scss']
})
export class LocationManagerPageComponent extends Tabbing<LocationManagerState> implements OnInit, OnDestroy {
  @Input() title: string;
  @Input() preloadedLocation: Location;
  @Input() managing = true;
  @Output() cancel = new EventEmitter<null>();
  @Output() selectLocation = new EventEmitter<RideRequestLocation>();

  searchString$: Observable<string> = this.locationManagerStore$.select(LocationManagerSelectors.getSearchString);
  loadingSearchResults$: Observable<boolean> = this.locationManagerStore$.select(LocationManagerSelectors.getLoadingSearchResults);
  autoCompleteResults$: Observable<Array<AutoCompleteResult>> = this.locationManagerStore$.select(
    LocationManagerSelectors.getAutocompleteResults
  );
  loadedLocation$: Observable<Location> = this.locationManagerStore$.select(LocationManagerSelectors.getLoadedLocation);
  mapLoaded$: Observable<boolean> = this.locationManagerStore$.select(LocationManagerSelectors.getMapLoaded);
  offsetLocationMode$: Observable<boolean> = this.locationManagerStore$.select(LocationManagerSelectors.getOffsetLocationMode);
  mapCenter$: Observable<IGeoCoords> = this.locationManagerStore$.select(LocationManagerSelectors.getMapCenterCoords);
  userLocation$: Observable<IGeoCoords> = this.locationManagerStore$.select(LocationManagerSelectors.getUserLocation);
  addingNewLocation$: Observable<boolean> = this.locationManagerStore$.select(LocationManagerSelectors.getAddingNewLocation);
  newLocationName$: Observable<string> = this.locationManagerStore$.select(LocationManagerSelectors.getNewLocationName);
  hoveredLocation$: Observable<Location | Sublocation> = this.locationManagerStore$.select(LocationManagerSelectors.getHoveredLocation);
  selectedLocation$: Observable<Location> = this.locationManagerStore$.select(LocationManagerSelectors.getSelectedLocation);
  geocodedOffsetLocation$: Observable<Location> = this.locationManagerStore$.select(LocationManagerSelectors.getGeocodedOffsetLocation);
  selectedLocationAutocompleteString$: Observable<string> = this.locationManagerStore$.select(
    LocationManagerSelectors.getSelectedLocationAutocompleteString
  );
  loading = false;

  constructor(
    protected readonly tabStore$: Store<TabsState>,
    private locationManagerStore$: Store<State<LocationManagerState>>,
    private dialog: MatDialog,
    private mapsService: MapsService,
    private locationService: LocationService
  ) {
    super(tabStore$);
  }

  ngOnInit() {
    if (!isNullOrUndefined(this.preloadedLocation)) {
      this.locationManagerStore$.dispatch(LocationManagerActions.SearchResultSelected({ locationLabel: this.preloadedLocation.display }));
      return;
    }

    this.selectedLocationAutocompleteString$.pipe(take(1)).subscribe(selectedLocationAutocompleteString => {
      if (!isNullOrUndefined(selectedLocationAutocompleteString) && selectedLocationAutocompleteString.length > 0) {
        this.locationManagerStore$.dispatch(
          LocationManagerActions.SearchResultSelected({ locationLabel: selectedLocationAutocompleteString })
        );
      }
    });
  }

  ngOnDestroy(): void {
    this.locationManagerStore$.dispatch(LocationManagerActions.Reset());
  }

  handleSearch(searchString: string): void {
    this.locationManagerStore$.dispatch(LocationManagerActions.Search({ searchString }));
  }

  handleClearSearch(): void {
    this.locationManagerStore$.dispatch(LocationManagerActions.ClearSearch());
  }

  handleAutocompleteResultSelected(result: AutoCompleteResult): void {
    this.locationManagerStore$.dispatch(LocationManagerActions.SearchResultSelected({ locationLabel: result.label }));
  }

  handleMapLoaded(): void {
    this.locationManagerStore$.dispatch(LocationManagerActions.MapLoaded());
  }

  handleOffsetLocationMode(mode: boolean): void {
    this.locationManagerStore$.dispatch(LocationManagerActions.UseOffsetLocation({ useOffset: mode }));
  }

  handleMapMoved(coords: IGeoCoords): void {
    this.locationManagerStore$.dispatch(LocationManagerActions.MoveMap({ coords }));
  }

  handleSelectLocation(location: Location) {
    this.locationManagerStore$.dispatch(LocationManagerActions.LocationSelected({ location }));
  }

  handleAddNewLocation(): void {
    this.locationManagerStore$.dispatch(LocationManagerActions.AddNewLocation());
  }

  handleCancelAddNewLocation(): void {
    this.locationManagerStore$.dispatch(LocationManagerActions.CancelAddNewLocation());
  }

  handleSaveNewLocation(description: string): void {
    if (description.length < 1) {
      this.locationManagerStore$.dispatch(
        LocationManagerActions.NewLocationInvalid({ error: new ApplicationException('The new location name cannot be empty') })
      );
    } else {
      this.locationManagerStore$.dispatch(LocationManagerActions.SetNewLocationName({ name: description }));
      this.locationManagerStore$.dispatch(LocationManagerActions.SubmitNewLocation());
    }
  }

  handleLocationMouseover(location: Location | Sublocation) {
    this.locationManagerStore$.dispatch(LocationManagerActions.LocationMouseover({ location }));
  }

  handleLocationMouseout(location: Location | Sublocation) {
    this.locationManagerStore$.dispatch(LocationManagerActions.LocationMouseout({ location }));
  }

  handleDeleteSublocation(sublocation: Sublocation) {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Delete sublocation?',
          message: 'This action is not reversible'
        }
      })
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed) {
          this.locationManagerStore$.dispatch(LocationManagerActions.DeleteSublocation({ sublocation }));
        }
      });
  }

  handleUseLocation(): void {
    this.loading = true;
    combineLatest([this.selectedLocation$, this.offsetLocationMode$, this.mapCenter$])
      .pipe(
        take(1),
        switchMap(([selectedLocation, offsetLocationMode, mapCenter]) => {
          if (!isNullOrUndefined(selectedLocation)) {
            this.loading = false;
            return of(this.selectLocation.emit(this.locationService.locationToRideRequestLocation(selectedLocation)));
          } else if (offsetLocationMode) {
            return this.mapsService.geocodeLocationByLatLng(mapCenter.lat, mapCenter.lng).pipe(
              switchMap(geocodedLocation =>
                this.locationService.addLocation(geocodedLocation.display).pipe(
                  map(savedLocation => {
                    this.selectLocation.emit(this.locationService.locationToRideRequestLocation(savedLocation));
                    this.loading = false;
                  })
                )
              )
            );
          }
        })
      )
      .subscribe();
  }

  getTabbingConfig(): Tab<LocationManagerState> {
    return {
      name: 'Location Management',
      matIcon: 'my_location',
      selectorState: LocationManagerSelectors.locationManagerState,
      restoreSnapshotAction: snapshot => LocationManagerActions.RestoreSnapshot({ state: snapshot })
    };
  }
}
